import { useContext, useEffect, useState } from "react";
import {
  Table,
  Tooltip,
  Card,
  DatePicker,
  Input,
  Button,
  Drawer,
  Space,
  message,
} from "antd";

import {
  VideoCameraTwoTone,
  PlayCircleTwoTone,
  FileTwoTone,
  SearchOutlined,
  LoadingOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";

import { Context } from "../../context";
import { Fetch } from "../../fetch";
import { convertIdToKey } from "../../utils";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

import "./styles.css";
import DownloadButton from "./DownloadButton";

const { Column } = Table;
const { RangePicker } = DatePicker;

const displayDateFormat = "YYYY-MM-DD";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const ContentViews = () => {
  const { token, apiUrl } = useContext(Context);
  const [data, setData] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalPageViews, setTotalPageViews] = useState(0);
  const [playRate, setPlayRate] = useState(0);
  const [filters, setFilters] = useState({
    date: [dayjs().add(-7, "d"), dayjs().add(-1, "d")],
    title: null,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
    hideOnSinglePage: true,
    showSizeChanger: false,
  });

  const [drawerPagination, setDrawerPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
    hideOnSinglePage: true,
    showSizeChanger: false,
  });

  const [contentTitle, setContentTitle] = useState();
  const [viewsData, setViewsData] = useState();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [downloadLink, setDownloadLink] = useState(null);
  const [exportStarted, setExportStarted] = useState(false);

  // unused for time being
  const isWithinLast30Days = (date) => {
    const today = dayjs().startOf("day");
    const thirtyDaysAgo = dayjs().subtract(30, "day").startOf("day");
    return dayjs(date).isAfter(thirtyDaysAgo) && dayjs(date).isBefore(today);
  };

  const disabledDate = (current) => {
    return dayjs(current).isAfter(dayjs(), "day");
  };

  const rangePresets = [
    {
      label: "Yesterday",
      value: [dayjs().add(-1, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 3 Days",
      value: [dayjs().add(-3, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 7 Days",
      value: [dayjs().add(-7, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 30 Days",
      value: [dayjs().add(-30, "d"), dayjs().add(-1, "d")],
    },
  ];

  useEffect(() => {
    if (episodes.length > 0) return;

    Fetch(`${apiUrl}/episodes/all`, token, (result) => {
      const episodes = convertIdToKey(result.episodes);
      setEpisodes(episodes);
    });
  }, [apiUrl, token]);

  useEffect(() => {
    setLoading(true);
    let start_date, end_date, date_string, search_string;
    if (filters.date[0] & filters.date[1]) {
      start_date = dayjs(filters.date[0]).format(displayDateFormat);
      end_date = dayjs(filters.date[1]).format(displayDateFormat);
      date_string = `start_date=${start_date}&end_date=${end_date}`;
    } else {
      date_string = "";
    }
    if (filters.title) {
      search_string = `content_title=${filters.title}`;
    } else {
      search_string = "";
    }
    let url = `${apiUrl}/reports/content_reports?page=${pagination.current}&limit=10&${date_string}&${search_string}`;

    Fetch(url, token, (result) => {
      if (result) {
        setData(result.data);
        setTotalViews(result.total_views);
        setTotalPageViews(result.total_page_views);
        if (result.total_page_views === 0) setPlayRate(0);
        else
          setPlayRate(
            Math.round((result.total_views / result.total_page_views) * 100)
          );
        setPagination((p) => ({
          ...pagination,
          total: result.total_row_count,
        }));
        setRefresh(false);
        setLoading(false);
      }
    });
  }, [apiUrl, token, refresh, pagination.current, filters]);

  useEffect(() => {
    if (contentTitle === undefined) {
      return;
    }
    Fetch(
      `${apiUrl}/views?filter_by=title&q=${encodeURIComponent(
        contentTitle
      )}&current=${drawerPagination.current}&size=${drawerPagination.pageSize}`,
      token,
      (result) => {
        if (result.success === true) {
          const views = result.data;
          views.forEach((view) => {
            view["view_start"] = dayjs(view.view_start).format(
              "DD MMM YYYY, hh:mm a"
            );
          });
          setDrawerPagination((p) => ({
            ...drawerPagination,
            total: result.total,
          }));
          setViewsData(result.data);
        }
      }
    );
  }, [apiUrl, token, drawerPagination.current]);

  const get_watch_time = (milliseconds) => {
    if (milliseconds === 0) {
      return " < 1";
    }
    let time = Math.round(milliseconds / 60000);
    if (time < 1) return " < 1";
    else if (time >= 1 && time <= 2) return "1";
    else return `${Math.round(milliseconds / 60000)}`;
  };

  const render_avg_watch_time = (watch_time, views) => {
    if (views === 0) return get_watch_time(0);
    return get_watch_time(watch_time ? watch_time / views : 0 / views);
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDrawerTableChange = (drawerPagination) => {
    setDrawerPagination(drawerPagination);
  };

  const returnSeriesSeason = (content_id) => {
    const ep = episodes.find((e) => e.key === content_id);
    return ep ? `${ep.series_name} - ${ep.season_number}` : "N/A";
  };

  const getViewsData = (title) => {
    Fetch(
      `${apiUrl}/views?filter_by=title&q=${encodeURIComponent(title)}&current=${
        drawerPagination.current
      }&size=${drawerPagination.pageSize}`,
      token,
      (result) => {
        if (result.success === true) {
          setContentTitle(title);
          const views = result.data;
          views.forEach((view) => {
            view["view_start"] = dayjs(view.view_start).format(
              "DD MMM YYYY, hh:mm a"
            );
          });
          setDrawerPagination((p) => ({
            ...drawerPagination,
            total: result.total,
          }));
          setViewsData(result.data);
          setDrawerVisible(true);
        }
      }
    );
  };

  const handleExport = () => {
    setExportStarted(true);
    Fetch(
      `${apiUrl}/exports/views?filter_by=title&q=${encodeURIComponent(
        contentTitle
      )}`,
      token,
      (result) => {
        if (result) {
          setDownloadLink(result);
          message.success("Files ready for download");
          setExportStarted(false);
        } else {
          message.error("Error Exporting Data");
          setExportStarted(false);
        }
      }
    );
  };

  return (
    <Card className="reports-wrapper" title={"Content Reports"}>
      <div className="reports-filters-wrapper">
        <RangePicker
          defaultValue={[filters.date[0], filters.date[1]]}
          onChange={(_, datestring) => {
            setFilters({
              ...filters,
              date: [dayjs(datestring[0]), dayjs(datestring[1])],
            });
          }}
          allowClear={false}
          presets={rangePresets}
          disabledDate={disabledDate}
          style={{ width: "60%" }}
        />
        <Input
          placeholder="Search for content"
          onChange={(e) => setFilters({ ...filters, title: e.target.value })}
          style={{ marginLeft: "1%" }}
          prefix={<SearchOutlined />}
        />
      </div>
      <div className="overview-wrapper">
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <FileTwoTone twoToneColor={"orange"} />
            <div>
              <strong>Total Page Views: </strong>
            </div>
            <div>{totalPageViews}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <VideoCameraTwoTone twoToneColor="green" />
            <div>
              <strong>Total Plays: </strong>
            </div>
            <div>{totalViews}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <PlayCircleTwoTone twoToneColor={"blue"} />
            <div>
              <strong>Play Rate: </strong>
            </div>
            <div>{playRate} %</div>
          </Card>
        </div>
      </div>
      <Table
        dataSource={data}
        loading={loading}
        size="small"
        pagination={pagination}
        onChange={handleTableChange}
      >
        <Column
          title="Content Title"
          align="left"
          dataIndex={"title"}
          render={(_, record) => (
            <Button
              type="link"
              onClick={() => {
                setContentTitle(record.title);
                getViewsData(record.title);
                setDrawerVisible(true);
              }}
            >
              {record.title.length > 50 ? (
                <Tooltip title={record.title}>
                  <span>{`${record.title.slice(0, 50)}...`}</span>
                </Tooltip>
              ) : (
                record.title
              )}
            </Button>
          )}
        />
        <Column
          title="Series & Season"
          align="center"
          render={(_, record) => returnSeriesSeason(record.content_id)}
        />
        <Column title="Total Views" align="center" dataIndex={"views"} />
        <Column
          title="Unique Views"
          align="center"
          dataIndex={"unique_views"}
        />
        <Column
          title="Total Watch Time (in Minutes)"
          align="center"
          dataIndex={"total_watch_time"}
          render={(_, record) => get_watch_time(record.total_watch_time)}
        />
        <Column
          title="Average Watch Time (in Minutes)"
          align="center"
          dataIndex={"avg_watch_time"}
          render={(_, record) =>
            render_avg_watch_time(record.total_watch_time, record.views)
          }
        />
      </Table>

      <Drawer
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setViewsData([]);
          setContentTitle();
          setDownloadLink(null);
        }}
        destroyOnClose={true}
        size="large"
        width={"1000px"}
        maskClosable={false}
        title={contentTitle}
        keyboard={false}
        closable={true}
        extra={
          <div>
            {!downloadLink ? (
              <Button onClick={handleExport} disabled={downloadLink !== null}>
                {exportStarted ? (
                  <LoadingOutlined />
                ) : (
                  <CloudDownloadOutlined />
                )}
                Export Views Data
              </Button>
            ) : (
              <Space direction="horizontal">
                <DownloadButton
                  url={downloadLink.xlsx}
                  fileName={`${contentTitle}.xlsx`}
                  filetype={"Spreadsheet"}
                />
                <DownloadButton
                  url={downloadLink.csv}
                  fileName={`${contentTitle}.csv`}
                  filetype={"CSV"}
                />
              </Space>
            )}
          </div>
        }
      >
        <Table
          dataSource={viewsData}
          pagination={drawerPagination}
          onChange={handleDrawerTableChange}
        >
          <Column title="View Start" align="center" dataIndex={"view_start"} />
          <Column
            title="User Email"
            align="left"
            dataIndex={"user_email"}
            render={(_, record) =>
              record.user_email ? record.user_email : "Anonymous"
            }
          />
          <Column
            title="Watch Time (minutes)"
            align="center"
            dataIndex={"watch_time"}
            render={(_, record) =>
              record.watch_time ? get_watch_time(record.watch_time) : "-"
            }
          />
        </Table>
      </Drawer>
    </Card>
  );
};

export default ContentViews;
